import React, { useState } from 'react';
import {
  Box,
  Link as DSLink,
  ListRow,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import { ColumnChip } from '../../../features/ViolationsTab/components/ViolationsTabList/ViolationsListTabShared';
import { CF_NEU_BLD } from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';

const getFormattedValue = (
  value: string | number | null | undefined,
  dateFormat: string
): string => {
  if (!value) {
    return '';
  }

  // Try to parse as date if it looks like a date string
  if (typeof value === 'string' && value.includes('GMT')) {
    try {
      const date = new Date(value);

      if (!Number.isNaN(date.getTime())) {
        return format(date, dateFormat);
      }
    } catch (e) {
      // If parsing fails, return the original string
    }
  }

  // Return as is if it's not a date or parsing failed
  return String(value);
};

type HistoryRow = Record<
  number,
  | {
      value: string | number | null;
    }
  | undefined
>;

interface ExpandableBlockProps {
  items: HistoryRow[];
  initialDisplayCount?: number;
}

/**
 * ExpandableBlock
 */
function ExpandableBlock({
  items,
  initialDisplayCount = 2,
}: ExpandableBlockProps) {
  const l = useInvertedLocalize();
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    aag: {
      global: { globalDateFormat },
    },
  } = useTypedSelector((state) => state);

  const itemsToShow = isExpanded ? items : items.slice(0, initialDisplayCount);

  const remainingCount = items.length - initialDisplayCount;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const globalFormatWithTime = `${globalDateFormat}, HH:mm`;

  return (
    <div className='expandable-block'>
      {itemsToShow.map((row, index) => (
        <ListRow
          key={row[0]?.value ?? `row-${index}`}
          className='SodSidePanelHistory-postBlock-ListRow'
          dataTestId='testId'
          isCheckboxDisabled
          centerContent={
            <Box className='SodSidePanelHistory-postBlock-centerContent'>
              <Typography
                kind='h3'
                desktop='h3-d'
                mobile='h3-d'
                color={CF_NEU_BLD}>
                {row[1]?.value ?? ''}
              </Typography>
              <Box className='SodSidePanelHistory-postBlock-centerContent-chip'>
                <ColumnChip
                  name={l('Start')}
                  value={getFormattedValue(row[2]?.value, globalFormatWithTime)}
                  isPrefixInterpoint
                />

                <ColumnChip
                  name={l('End')}
                  value={getFormattedValue(row[3]?.value, globalFormatWithTime)}
                  isPrefixInterpoint
                />

                <ColumnChip
                  name={l('Added By')}
                  value={row[4]?.value?.toString() ?? ''}
                  isPrefixInterpoint
                />
                <ColumnChip
                  name={l('Comments')}
                  value={row[5]?.value?.toString() ?? '-'}
                  isPrefixInterpoint={false}
                />
              </Box>
            </Box>
          }
        />
      ))}

      {items.length > initialDisplayCount && (
        <Box tag='div' className=''>
          {isExpanded ? (
            <DSLink
              text='Show Less'
              kind='button'
              onClick={toggleExpanded}
              suffixIconKind='ChevronUpSmall'
            />
          ) : (
            <DSLink
              text={`+${remainingCount} More`}
              kind='button'
              onClick={toggleExpanded}
              suffixIconKind='ChevronDownSmall'
            />
          )}
        </Box>
      )}
    </div>
  );
}

ExpandableBlock.defaultProps = {
  initialDisplayCount: 2,
};

export default ExpandableBlock;
